import React, { Component } from 'react';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';
import 'moment-timezone';
import toastr from 'toastr';
import Swal from 'sweetalert2';
import {
    arraysHaveSameElements,
    dateComparator,
    getUserDetails,
    getUserRole,
    isUsingAnalyteAbbreviationColumnImport
} from '../../../services/common/util';
import SearchMenu from './searchMenu';
import CheckboxRenderer from './checkboxRenderer';
import { Stepper, Step, StepLabel } from '@mui/material';
import { createAudit } from '../../../services/clinicPortalServices/auditService';
import { auditEnums } from '../../../services/common/constants';
import Papa from 'papaparse';
import { ThemeContext } from '../../../theme/ThemeProvider';
import store from '../../../redux/store';
import actions from '../../../redux/actions';
import { bindActions } from 'redux-zero/utils';
import { colorSchemeDark, colorSchemeLight, themeQuartz } from 'ag-grid-community';
import { uploadResultDocument, processResults } from '../../../services/clinicPortalServices/resultImportService';
import { fetchClientImportTemplates } from '../../../services/clinicPortalServices/clientImportTemplateService';
import { getAllAnalytes } from '../../../services/clinicPortalServices/analyteService';

const themeLight = themeQuartz.withPart(colorSchemeLight);
const themeDark = themeQuartz.withPart(colorSchemeDark);

const boundActions = bindActions(actions, store);

class ClinicResultImportGrid extends Component {
    constructor(props) {
        super(props);

        // Check user permissions
        const userRole = getUserRole();
        const allowedRoles = ['superadmin', 'admin', 'labtech'];
        const hasPermission = allowedRoles.some((role) => userRole.toLowerCase().includes(role));

        this.state = {
            hasPermission,
            paginationPageSize: 20,
            quickFilterText: '',
            gridName: 'Default',
            pageSize: '',
            columnDefs: [
                {
                    minWidth: 100,
                    children: [
                        {
                            headerName: 'Actions',
                            minWidth: 100,
                            field: 'checked',
                            cellRenderer: CheckboxRenderer
                        }
                    ]
                },
                {
                    headerName: 'Import Status',
                    minWidth: 140,
                    field: 'import_status',
                    valueGetter: (params) => {
                        return params.data.import_status || 'New';
                    }
                },
                {
                    headerName: 'Sample Info',
                    children: [
                        {
                            headerName: 'Sample ID',
                            field: 'sample_id',
                            minWidth: 150
                        },
                        {
                            headerName: 'Sample Name',
                            field: 'sample_name',
                            minWidth: 150
                        }
                    ]
                },
                {
                    headerName: 'Test Info',
                    children: [
                        {
                            headerName: 'Analyte Name',
                            field: 'analyteName',
                            minWidth: 200
                        },
                        {
                            headerName: 'Analyte Result',
                            field: 'analyteResult',
                            minWidth: 150
                        },
                        {
                            headerName: 'Units',
                            field: 'conc_units',
                            minWidth: 100
                        }
                    ]
                },
                {
                    headerName: 'Quality Metrics',
                    children: [
                        {
                            headerName: 'Quality',
                            field: 'quality',
                            minWidth: 100
                        },
                        {
                            headerName: 'Retention Time',
                            field: 'retention_time',
                            minWidth: 120
                        }
                    ]
                },
                {
                    headerName: 'Acquisition Info',
                    children: [
                        {
                            headerName: 'Date & Time',
                            field: 'acquisition_date_time',
                            minWidth: 180,
                            comparator: dateComparator,
                            valueGetter: (params) => {
                                if (params.data.acquisition_date_time) {
                                    return moment(params.data.acquisition_date_time).format('MM/DD/YYYY hh:mm A');
                                }
                            }
                        },
                        {
                            headerName: 'Operator',
                            field: 'operator_name',
                            minWidth: 150
                        }
                    ]
                }
            ],
            defaultColDef: {
                resizable: true,
                sortable: true,
                filter: true,
                floatingFilter: false,
                filterParams: {
                    maxNumConditions: 1
                }
            },
            components: {
                checkboxRenderer: CheckboxRenderer
            },
            context: { componentParent: this },
            excelStyles: [],
            rowData: [],
            tempImportedData: [],
            result_value: 'all',
            steps: ['Upload CSV file', 'Review imported data', 'Process and save results'],
            activeStep: 0,
            documentType: 'lab-results',
            filename: '',
            templateFilesInfo: [],
            allAnalytes: [],
            showDataFlag: false
        };
    }

    componentDidMount() {
        if (!this.state.hasPermission) {
            toastr.error("You don't have permission to access this page");
            this.props.history.push('/clinic/dashboard');
        }
        fetchClientImportTemplates().then((res) => {
            if (res && res.data && res.data.length > 0) {
                this.setState({ templateFilesInfo: res.data });
            }
        });
        getAllAnalytes().then((res) => {
            if (res && res.data) {
                this.setState({ allAnalytes: res.data });
            }
        });
    }

    onGridReady = (params) => {
        this.gridApi = params.api;
    };

    loadGridData = () => {
        this.gridApi.showLoadingOverlay();
        this.gridApi.showNoRowsOverlay();
        this.gridApi.hideOverlay();
    };

    onFilterTextChange = (e) => {
        this.setState({ quickFilterText: e.target.value });
    };

    onBtnExport = () => {
        this.gridApi.exportDataAsExcel({
            fileName: 'Result_Import_Grid_' + moment().format('YYYY_MM_DD_HH_mm_ss') + '.xlsx',
            sheetName: 'Results'
        });
        const userData = JSON.parse(getUserDetails());
        const auditData = {
            identifier: auditEnums.IDENTIFIERS.ExportRecord,
            event_type: auditEnums.EVENTTYPES.ResultImportGridExported,
            user_id: userData._id,
            user_name: userData.user_name + ' (' + userData.role + ')',
            update_string: 'Result Import Grid Exported'
        };
        createAudit(auditData);
    };

    onPageSizeChanged = () => {
        let value = document.getElementById('page-size').value;
        this.setState({ paginationPageSize: Number(value) });
    };

    resetState = () => {
        this.gridApi.resetColumnState();
    };

    importResults = async (e) => {
        boundActions.startBlockUILoading();
        const file = e.target.files[0];
        if (file) {
            const fileName = file.name;

            try {
                // Parse CSV file
                Papa.parse(file, {
                    header: false,
                    complete: async (results) => {
                        let rows = results.data;
                        if (rows.length === 0) {
                            boundActions.endBlockUILoading();
                            toastr.error('No data found in the file');
                            return;
                        }
                        let fileColumnCount = Math.max(...rows.map((arr) => arr.length));
                        let templateFiles = this.state.templateFilesInfo.filter(
                            (item) => fileColumnCount === item.fileInfo.columnCount
                        );
                        let matchingTemplate = null;
                        for (let i = 0; i < templateFiles.length; i++) {
                            let tempRows = rows.slice(templateFiles[i].fileInfo.headerRowIndex || 0);
                            if (
                                arraysHaveSameElements(
                                    tempRows[0],
                                    templateFiles[i].columnMappings.map((item) => item.sourceColumnName)
                                )
                            ) {
                                matchingTemplate = templateFiles[i];
                            }
                        }
                        let transformedData = [];

                        if (!matchingTemplate && !isUsingAnalyteAbbreviationColumnImport()) {
                            boundActions.endBlockUILoading();
                            toastr.error('There is no matching template with this.');
                            return;
                        } else if (!matchingTemplate && isUsingAnalyteAbbreviationColumnImport()) {
                            if (
                                rows[0][1] === 'S.No.' &&
                                rows[0][2] === 'Rack No.' &&
                                rows[0][3] === 'Cup Pos.' &&
                                rows[0][4] === 'Type' &&
                                rows[0][5] === 'S.ID' &&
                                rows[0][6] === 'Kind No.' &&
                                rows[0][7] === 'Org.S.No.'
                            ) {
                                let acquisitionDate =
                                    moment(rows[rows.length - 1][1], 'YYYYMMDDHHmm').format('YYYYMMDD') !==
                                    'Invalid date'
                                        ? moment(rows[rows.length - 1][1], 'YYYYMMDDHHmm').format('YYYY-MM-DD HH:mm:ss')
                                        : '';
                                this.setState({ showDataFlag: true });
                                rows[0].map((columnName, columnIndex) => {
                                    let abbreviationFromColumnName = columnName.split('.')[1];
                                    let matchingAnalyte = this.state.allAnalytes.find(
                                        (analyte) =>
                                            analyte.abbreviation && analyte.abbreviation === abbreviationFromColumnName
                                    );
                                    if (matchingAnalyte) {
                                        for (let i = 1; i < rows.length - 1; i++) {
                                            let row = rows[i];
                                            transformedData.push({
                                                id: transformedData.length + 1,
                                                checked: true,
                                                sample_id: row[5],
                                                sample_name: '',
                                                component_name: matchingAnalyte?.name || '',
                                                analyteName: matchingAnalyte?.name || '',
                                                calculated_concentration: row[columnIndex] || '',
                                                analyteResult: row[columnIndex] || '',
                                                conc_units: matchingAnalyte?.units || '',
                                                units: matchingAnalyte?.units || '',
                                                quality: '',
                                                retention_time: '',
                                                acquisition_date_time: acquisitionDate || '',
                                                operator_name: '',
                                                raw_data: row,
                                                instrument_id: '68506802320a063e713be2ab' || '',
                                                import_status: 'New',
                                                data_flag: row[columnIndex + 1] || ''
                                            });
                                        }
                                    }
                                    return null;
                                });
                            } else {
                                boundActions.endBlockUILoading();
                                toastr.error('There is no matching template with this.');
                                return;
                            }
                        } else {
                            let acquisitionDate =
                                moment(rows[2][1]).format('YYYYMMDD') !== 'Invalid date' ? rows[2][1] : '';
                            let columnRow = rows[matchingTemplate.fileInfo.headerRowIndex || 0];
                            rows = rows.slice((matchingTemplate.fileInfo.headerRowIndex || 0) + 1);
                            rows = rows.map((row) => {
                                let rowWithHeader = {};
                                matchingTemplate.columnMappings.forEach((mapping, mappingIndex) => {
                                    let systemColumnName = mapping.systemColumnName;
                                    if (systemColumnName === 'None' && columnRow[mappingIndex] !== 'Sample ID') {
                                        systemColumnName = columnRow[mappingIndex];
                                    }
                                    rowWithHeader[systemColumnName] = row[mappingIndex];
                                });
                                return rowWithHeader;
                            });

                            // Transform data for our system
                            transformedData = rows.map((row, index) => {
                                // Extract sample ID - first check direct columns, then MPX format
                                let sampleId = row['Sample ID'] || row['sample_id'] || row['SampleID'] || null;

                                let matchingAnalyte = this.state.allAnalytes.find(
                                    (analyte) =>
                                        analyte.name.toLowerCase() === row['Analyte Name']?.toLowerCase() ||
                                        (isUsingAnalyteAbbreviationColumnImport() &&
                                            analyte.name.toLowerCase() + ' 1' === row['Analyte Name']?.toLowerCase()) ||
                                        matchingTemplate.analyteMappings.find(
                                            (item) =>
                                                item.systemAnalyteId === analyte._id &&
                                                item.sourceAnalyte?.toLowerCase() === row['Analyte Name']?.toLowerCase()
                                        )
                                );
                                if (matchingAnalyte) {
                                    return {
                                        id: index + 1,
                                        checked: true,
                                        sample_id: sampleId,
                                        sample_name: row['Sample Name'] || '',
                                        component_name: matchingAnalyte?.name || row['Analyte Name'] || '',
                                        analyteName: matchingAnalyte?.name || row['Analyte Name'] || '',
                                        calculated_concentration: row['Analyte Result'] || row['Analyte Result'] || '',
                                        analyteResult: row['Analyte Result'] || row['Analyte Result'] || '',
                                        conc_units: row['Conc. Units'] || matchingAnalyte?.units || '',
                                        units: row['Conc. Units'] || matchingAnalyte?.units || '',
                                        quality: row['Quality'] || '',
                                        retention_time: row['Retention Time'] || '',
                                        acquisition_date_time: row['Acquisition Date & Time'] || acquisitionDate || '',
                                        operator_name: row['Operator Name'] || '',
                                        // Store all original data
                                        raw_data: row,
                                        instrument_id: matchingTemplate.instrumentId?._id || '',
                                        import_status: 'New'
                                    };
                                } else {
                                    return {};
                                }
                            });
                        }

                        // Filter out rows without sample IDs
                        const validRows = transformedData.filter((row) => row.sample_id);
                        const invalidRows = transformedData.filter((row) => !row.sample_id);

                        if (invalidRows.length > 0) {
                            console.warn(`${invalidRows.length} rows without valid sample IDs`);
                        }

                        this.setState({
                            rowData: validRows,
                            tempImportedData: validRows,
                            activeStep: 1,
                            filename: fileName
                        });

                        boundActions.endBlockUILoading();

                        Swal.fire({
                            customClass: {
                                container:
                                    window.localStorage.getItem('appTheme') === 'Dark' &&
                                    /clinic|lims/.test(window.location.pathname.split('/')[1]) &&
                                    'dark-swal'
                            },
                            title: 'CSV File Imported Successfully',
                            html: `You have imported ${validRows.length} valid records.<br/>
                               ${
                                   invalidRows.length > 0
                                       ? `${invalidRows.length} records were skipped (no sample ID).<br/>`
                                       : ''
                               }
                               Please review the records and click Next to continue.`,
                            icon: 'success',
                            confirmButtonText: 'Ok',
                            allowOutsideClick: false
                        });

                        // Upload file for record keeping
                        let formData = new FormData();
                        formData.append('document', file, fileName);
                        uploadResultDocument(formData).then((success) => {
                            console.log('File uploaded successfully');
                        });
                    },
                    error: (error) => {
                        boundActions.endBlockUILoading();
                        toastr.error('Error parsing CSV file: ' + error.message);
                    }
                });
            } catch (error) {
                boundActions.endBlockUILoading();
                toastr.error('Error importing file: ' + error.message);
            }
        }
        e.target.value = '';
    };

    handleNext = () => {
        const currentStep = this.state.activeStep;
        if (currentStep === 2) {
            // Final step - process results
            this.processAndSaveResults();
        } else {
            this.setState({ activeStep: currentStep + 1 });
        }
    };

    handleBack = () => {
        const currentStep = this.state.activeStep;
        if (currentStep > 0) {
            this.setState({ activeStep: currentStep - 1 });
        }
    };

    selectAll = () => {
        const allChecked = this.state.rowData.every((item) => item.checked);
        const updatedData = this.state.rowData.map((item) => ({
            ...item,
            checked: !allChecked
        }));
        this.setState({ rowData: updatedData });
    };

    checkItem = (itemId) => {
        const updatedData = this.state.rowData.map((item) => {
            if (item.id === itemId) {
                return { ...item, checked: !item.checked };
            }
            return item;
        });
        this.setState({ rowData: updatedData });
    };

    processAndSaveResults = async () => {
        const checkedItems = this.state.rowData.filter((item) => item.checked);

        if (checkedItems.length === 0) {
            toastr.warning('Please select at least one item to process');
            return;
        }

        // boundActions.startBlockUILoading();

        try {
            processResults({
                results: checkedItems,
                userDetails: JSON.parse(getUserDetails()),
                filename: this.state.filename
            });
            // boundActions.endBlockUILoading();
            toastr.success('Processing will take a few minutes, feel free to leave the page');
            this.setState({
                rowData: [],
                tempImportedData: [],
                activeStep: 0
            });
            // const response = await processResults({
            //     results: checkedItems,
            //     userDetails: JSON.parse(getUserDetails()),
            //     filename: this.state.filename
            // });

            // console.log('Process results response:', response);

            // // Check if response is directly the data (not wrapped)
            // const data = response?.data || response;

            // if (data && (data.success || data.processed !== undefined)) {
            //     boundActions.endBlockUILoading();

            //     Swal.fire({
            //         customClass: {
            //             container:
            //                 window.localStorage.getItem('appTheme') === 'Dark' &&
            //                 /clinic|lims/.test(window.location.pathname.split('/')[1]) &&
            //                 'dark-swal'
            //         },
            //         title: 'Results Processed Successfully',
            //         html: `${data.processed} results were successfully imported.<br/>
            //                ${data.processedQCResult > 0 ? `${data.processedQCResult} are QC results here<br/>` : ''}
            //                ${data.failed > 0 ? `${data.failed} results had errors.<br/>` : ''}
            //                ${data.skipped > 0 ? `${data.skipped} results were skipped.<br/>` : ''}`,
            //         icon: 'success',
            //         confirmButtonText: 'Ok',
            //         allowOutsideClick: false
            //     }).then(() => {
            //         // Reset the component
            //         this.setState({
            //             rowData: [],
            //             tempImportedData: [],
            //             activeStep: 0
            //         });
            //     });
            // } else {
            //     boundActions.endBlockUILoading();
            //     console.error('Unexpected response format:', response);
            //     toastr.error('Unexpected response format from server');
            // }
        } catch (error) {
            boundActions.endBlockUILoading();
            toastr.error('Error processing results: ' + error.message);
        }
    };

    importFile = () => {
        document.getElementById('import_result_file').click();
    };

    render() {
        if (!this.state.hasPermission) {
            return (
                <div className="container">
                    <div className="alert alert-danger">You don't have permission to access this page.</div>
                </div>
            );
        }
        let columnDefs = this.state.columnDefs;
        if (this.state.showDataFlag && columnDefs[3] && columnDefs[3].children && columnDefs[3].children.length) {
            columnDefs[3].children[3] = {
                headerName: 'Data Flag',
                field: 'data_flag',
                minWidth: 100
            };
        }

        return (
            <div className="clinic-contain">
                <SearchMenu
                    onFilterTextChange={this.onFilterTextChange}
                    onPageSizeChanged={this.onPageSizeChanged}
                    resetState={this.resetState}
                    onBtnExport={this.onBtnExport}
                    selectAll={this.selectAll}
                    checkItem={this.checkItem}
                    importing={store.getState().blockUILoading}
                    importResults={this.importResults}
                    rowData={this.state.rowData}
                    activeStep={this.state.activeStep}
                    tempImportedData={this.state.tempImportedData}
                    importFile={this.importFile}
                />
                {this.state.activeStep > 0 && (
                    <div style={{ padding: '15px' }}>
                        <Stepper activeStep={this.state.activeStep}>
                            {this.state.steps.map((label, index) => {
                                return (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                );
                            })}
                        </Stepper>
                        <div className="row mt-3">
                            <div className="col-12 d-flex justify-content-end">
                                {this.state.activeStep > 0 && (
                                    <button className="btn btn-secondary button-info-grid" onClick={this.handleBack}>
                                        <i className="fa fa-arrow-left mr-2"></i> Back
                                    </button>
                                )}
                                {this.state.rowData.length > 0 && this.state.activeStep === 1 && (
                                    <button className="btn btn-info button-info-grid" onClick={() => this.selectAll()}>
                                        <i className="fa fa-check mr-2"></i>
                                        {this.state.rowData.filter((item) => !item.checked).length
                                            ? 'Select All'
                                            : 'Deselect All'}
                                    </button>
                                )}
                                <button
                                    className="btn btn-primary button-info-grid"
                                    onClick={this.handleNext}
                                    disabled={
                                        this.state.activeStep === 1 &&
                                        !this.state.rowData.filter((item) => item.checked).length
                                    }
                                >
                                    <i className="fa fa-arrow-right mr-2"></i>
                                    {this.state.activeStep === 2 ? 'Process Results' : 'Next'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {this.state.activeStep > 0 && (
                    <div style={{ width: '100%', height: 'calc(100vh - 350px)', padding: '15px' }}>
                        <ThemeContext.Consumer>
                            {({ themeName }) => (
                                <div id="myGrid" style={{ height: '100%', width: '100%' }}>
                                    <AgGridReact
                                        columnDefs={columnDefs}
                                        defaultColDef={this.state.defaultColDef}
                                        onGridReady={this.onGridReady}
                                        rowData={this.state.rowData}
                                        components={this.state.components}
                                        pagination={true}
                                        paginationPageSize={this.state.paginationPageSize}
                                        paginationPageSizeSelector={false}
                                        excelStyles={this.state.excelStyles}
                                        context={this.state.context}
                                        overlayNoRowsTemplate="No Items To Show"
                                        theme={themeName === 'Light' ? themeLight : themeDark}
                                        quickFilterText={this.state.quickFilterText}
                                    />
                                </div>
                            )}
                        </ThemeContext.Consumer>
                    </div>
                )}
            </div>
        );
    }
}

export default ClinicResultImportGrid;
